import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Offcanvas, Form } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import CustomInputWithLabel from "src/layouts/form/CustomInputWithLabel";
import {
  BookingVisa,
  BookingVisaEditForm,
  VISA_TYPE_LABEL,
  COUNTRY_LIST,
  downloadVisaDocument,
  editVisaBooking,
  sanitizeFileName,
} from "src/store/reducers/bookings";
import { useAppDispatch } from "src/store/hooks";
import CustomDatePickerWithLabel from "src/layouts/form/CustomDateTimeInputWithLabel";
import { KeyRequired } from "src/layouts/form/validations/Validation";
import { FormikAPIErrorHandler } from "src/utils/errorUtil";
import CustomSelectWithLabel from "src/layouts/form/CustomSelectWithLabel";
import CustomTextAreaWithLabel from "src/layouts/form/CustomTextAreaWithLabel";

const ACCEPTED_FILE_TYPES = ".jpg,.jpeg,.png,.pdf";

type IEditVisaBookingCanvas = {
  Title: string;
  editId: string;
  bookingId: string;
  hide: () => void;
  data: BookingVisa;
};

const EditVisaBookingCanvas = ({
  Title,
  editId,
  bookingId,
  hide,
  data,
}: IEditVisaBookingCanvas) => {
  const [pending, setPending] = useState(false);
  const dispatch = useAppDispatch();
  const [newFiles, setNewFiles] = useState<File[]>([]);
  const [downloading, setDownloading] = useState<string>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!data) {
      hide();
    }
  }, [data, hide]);

  const {
    handleChange,
    values,
    handleSubmit,
    resetForm,
    setFieldValue,
    touched,
    setErrors,
    errors,
  } = useFormik<{
    visa_holder_name: string;
    visa_number: string;
    visa_type: number;
    issuing_country: string;
    application_requirements: string;
    validity_period: string;
    fees: string;
    currency: string;
    advance_amount_paid: string;
    processing_time: string;
    additional_notes: string;
  }>({
    initialValues: {
      visa_holder_name: data?.visa_holder_name || "",
      visa_number: data?.visa_number || "",
      visa_type: data?.visa_type ?? 1,
      issuing_country: data?.issuing_country || "",
      application_requirements: data?.application_requirements || "",
      validity_period: data?.validity_period || "",
      fees: data?.fees || "",
      currency: data?.currency || "",
      advance_amount_paid: data?.advance_amount_paid || "",
      processing_time: data?.processing_time || "",
      additional_notes: data?.additional_notes || "",
    },
    enableReinitialize: true,
    validationSchema: Yup.object({
      visa_holder_name: KeyRequired("Visa holder name"),
      visa_number: KeyRequired("Visa number"),
      visa_type: KeyRequired("Visa type"),
      issuing_country: KeyRequired("Issuing country"),
    }),
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: (values) => {
      setPending(true);
      editVisa({ ...values, id: editId, booking: data.booking });
    },
  });

  const editVisa = useCallback(
    async (values: BookingVisaEditForm) => {
      try {
        await dispatch(
          editVisaBooking({ form: values, files: newFiles })
        ).unwrap();
        toast.success("Visa is updated successfully");
        hide();
      } catch (error: any) {
        FormikAPIErrorHandler(error.message, setErrors, toast.error);
      } finally {
        setPending(false);
      }
    },
    [dispatch, hide, newFiles, setErrors]
  );

  useEffect(() => {
    resetForm();
    setNewFiles([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, [resetForm, editId]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const addedFiles = Array.from(e.target.files);
      setNewFiles((prev) => [...prev, ...addedFiles]);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const removeNewFile = (index: number) => {
    setNewFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleDownload = useCallback(
    async (documentId: string) => {
      try {
        setDownloading(documentId);
        const url = await dispatch(
          downloadVisaDocument({
            booking_id: bookingId,
            visa_id: data.id,
            document_id: documentId,
          })
        ).unwrap();
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", documentId);
        document.body.appendChild(link);
        link.click();
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        }, 200);
      } catch (e) {
        toast.error("Failed to download document");
      } finally {
        setDownloading(undefined);
      }
    },
    [bookingId, data, dispatch]
  );

  return (
    <Offcanvas
      show={!!editId}
      onHide={hide}
      className="offcanvas-end customeoff"
      placement="end"
    >
      <div className="offcanvas-header">
        <h3 className="modal-title" id="#gridSystemModal">
          {Title}
        </h3>
        <button type="button" className="btn-close" onClick={hide}>
          <i className="fa-solid fa-xmark"></i>
        </button>
      </div>
      <div className="offcanvas-body">
        <div className="container-fluid">
          <form onSubmit={handleSubmit}>
            <div className="row">
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="visa_holder_name"
                  labelText="Visa Holder Name"
                  required={true}
                  handleChange={handleChange}
                  value={values.visa_holder_name}
                  touched={touched.visa_holder_name}
                  errors={errors.visa_holder_name}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="visa_number"
                  labelText="Visa Number"
                  required={true}
                  handleChange={handleChange}
                  value={values.visa_number}
                  touched={touched.visa_number}
                  errors={errors.visa_number}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomSelectWithLabel
                  id="visa_type"
                  labelText="Visa Type"
                  required={true}
                  handleChange={handleChange}
                  value={values.visa_type}
                  touched={touched.visa_type}
                  errors={errors.visa_type}
                >
                  {Object.entries(VISA_TYPE_LABEL).map(([key, label]) => (
                    <option value={key} key={key}>
                      {label}
                    </option>
                  ))}
                </CustomSelectWithLabel>
              </div>
              <div className="col-xl-6 mb-3">
                <CustomSelectWithLabel
                  id="issuing_country"
                  labelText="Issuing Country"
                  required={true}
                  handleChange={handleChange}
                  value={values.issuing_country}
                  touched={touched.issuing_country}
                  errors={errors.issuing_country}
                >
                  <option value="">Select Country</option>
                  {COUNTRY_LIST.map((country) => (
                    <option value={country.country_name} key={country.iso_alpha2}>
                      {country.country_name}
                    </option>
                  ))}
                </CustomSelectWithLabel>
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="validity_period"
                  labelText="Validity Date"
                  inputType="date"
                  handleChange={handleChange}
                  value={values.validity_period}
                  touched={touched.validity_period}
                  errors={errors.validity_period}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="processing_time"
                  labelText="Processing Time"
                  handleChange={handleChange}
                  value={values.processing_time}
                  touched={touched.processing_time}
                  errors={errors.processing_time}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="fees"
                  labelText="Fees"
                  handleChange={handleChange}
                  value={values.fees}
                  touched={touched.fees}
                  errors={errors.fees}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="currency"
                  labelText="Currency"
                  handleChange={handleChange}
                  value={values.currency}
                  touched={touched.currency}
                  errors={errors.currency}
                />
              </div>
              <div className="col-xl-6 mb-3">
                <CustomInputWithLabel
                  id="advance_amount_paid"
                  labelText="Advance Amount Paid"
                  handleChange={handleChange}
                  value={values.advance_amount_paid}
                  touched={touched.advance_amount_paid}
                  errors={errors.advance_amount_paid}
                />
              </div>
              <div className="col-xl-12 mb-3">
                <CustomTextAreaWithLabel
                  id="application_requirements"
                  labelText="Application Requirements"
                  handleChange={handleChange}
                  value={values.application_requirements}
                  touched={touched.application_requirements}
                  errors={errors.application_requirements}
                />
              </div>
              <div className="col-xl-12 mb-3">
                <CustomTextAreaWithLabel
                  id="additional_notes"
                  labelText="Additional Notes"
                  handleChange={handleChange}
                  value={values.additional_notes}
                  touched={touched.additional_notes}
                  errors={errors.additional_notes}
                />
              </div>

              <div className="col-xl-12 mb-3">
                <Form.Label>Existing Documents</Form.Label>
                {data?.documents &&
                Object.keys(data.documents).length > 0 ? (
                  <div className="mb-2">
                    {Object.entries(data.documents).map(([docName]) => (
                      <div
                        key={docName}
                        className="d-flex align-items-center justify-content-between mb-1 p-2 bg-light rounded"
                      >
                        <span className="text-truncate me-2">{docName}</span>
                        <button
                          type="button"
                          className="btn btn-primary btn-xs"
                          disabled={downloading === docName}
                          onClick={() => handleDownload(docName)}
                        >
                          {downloading === docName ? (
                            <i className="fa-solid fa-spinner" />
                          ) : (
                            <i className="fa-solid fa-download" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted mb-2">No existing documents</p>
                )}
              </div>

              <div className="col-xl-12 mb-3">
                <Form.Label>Add New Documents</Form.Label>
                <Form.Control
                  type="file"
                  accept={ACCEPTED_FILE_TYPES}
                  multiple
                  ref={fileInputRef}
                  onChange={handleFileChange}
                />
                <Form.Text className="text-muted">
                  Accepted formats: JPG, JPEG, PNG, PDF (Max 10 files, 5MB each)
                </Form.Text>
                {newFiles.length > 0 && (
                  <div className="mt-2">
                    {newFiles.map((file, index) => (
                      <div
                        key={index}
                        className="d-flex align-items-center justify-content-between mb-1 p-2 bg-light rounded"
                      >
                        <span className="text-truncate me-2">
                          {sanitizeFileName(file.name)}
                        </span>
                        <button
                          type="button"
                          className="btn btn-danger btn-xs"
                          onClick={() => removeNewFile(index)}
                        >
                          <i className="fas fa-x" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <button
              type="submit"
              disabled={pending}
              className="btn btn-primary me-1"
            >
              Submit
            </button>
            <Link to={"#"} onClick={hide} className="btn btn-danger light ms-1">
              Cancel
            </Link>
          </form>
        </div>
      </div>
    </Offcanvas>
  );
};

export default EditVisaBookingCanvas;
